import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { SidePanel } from 'design-system-web';
import { Alert } from 'components/lds';
import { TranslationKeys } from 'localization';
import { createCoverageHours } from 'graphql-queries';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { useAssignableMembers } from 'lane-shared/domains/workOrder/hooks/useAssignableMembers';
import { Channel } from 'application-types';
import {
  getBrowserTimeZone,
  getChannelTimezone,
} from 'domains/workOrder/serviceRequest/helpers/getChannelTimezone';
import { TeamCoverageEntry } from './coverageHoursTeamUtils';
import { ALL_ISSUE_TYPES_VALUE, resolveIssues } from './coverageHoursPanelUtils';
import { toProtoDayNames } from './coverageHoursRowFormat';
import { minutesOfDay } from './coverageHoursTimeUtils';
import { useCoverageHoursPanelOptions } from './useCoverageHoursPanelOptions';
import { CoverageHoursEntry } from './useCoverageHours';
import { CreateCoverageHours } from './CreateCoverageHours';
import { CoverageHoursPanelFooter } from './CoverageHoursPanelFooter';

const TRANSLATION_KEYS: Record<'title' | 'saveError', TranslationKeys> =
  Object.freeze({
    title: 'web.admin.serviceRequest.settings.coverageHours.create.title',
    saveError: 'web.admin.serviceRequest.settings.coverageHours.create.saveError',
  });

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
  channel?: Channel;
  categories: WorkOrderModuleCategoriesSettings[];
  onOptimisticAdd: (rows: CoverageHoursEntry[]) => void;
  onCreateSuccess: () => void;
  onCreateError: (localIds: string[]) => void;
};

export function CreateCoverageHoursPanel({
  isOpen,
  onClose,
  channelId,
  channel,
  categories,
  onOptimisticAdd,
  onCreateSuccess,
  onCreateError,
}: Props) {
  const { t } = useTranslation();
  const [, , assignableTeams] = useAssignableMembers(channelId);
  const [categoryValue, setCategoryValue] = useState<string | null>(null);
  const [issueTypeValue, setIssueTypeValue] = useState<string>(
    ALL_ISSUE_TYPES_VALUE
  );
  const [savedTeams, setSavedTeams] = useState<TeamCoverageEntry[]>([]);
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const { selectedCategory } = useCoverageHoursPanelOptions(
    categories,
    categoryValue
  );
  const [runCreateCoverageHours, { loading: isSaving }] =
    useMutation(createCoverageHours);

  const hasUnsavedChanges = savedTeams.length > 0;
  const canSave = Boolean(categoryValue) && savedTeams.length > 0 && !isSaving;

  useEffect(() => {
    if (!isOpen) {
      setIsConfirmingClose(false);
    }
  }, [isOpen]);

  const handleCategoryChange = (value: string) => {
    setCategoryValue(value);
    setIssueTypeValue(ALL_ISSUE_TYPES_VALUE);
  };

  const handleClose = () => {
    setCategoryValue(null);
    setIssueTypeValue(ALL_ISSUE_TYPES_VALUE);
    setSavedTeams([]);
    setIsConfirmingClose(false);
    setSaveError(false);
    onClose();
  };

  const handleSaveTeam = (entry: TeamCoverageEntry) => {
    setSavedTeams(prev => {
      const idx = prev.findIndex(team => team.id === entry.id);

      if (idx >= 0) {
        const next = [...prev];

        next[idx] = entry;

        return next;
      }

      return [...prev, entry];
    });
  };

  const handleOnCancel = () => {
    if (hasUnsavedChanges) {
      setIsConfirmingClose(true);
    } else {
      handleClose();
    }
  };

  const handleSave = async () => {
    if (!categoryValue) {
      return;
    }

    setSaveError(false);

    let optimisticRows: CoverageHoursEntry[] = [];

    try {
      const issues = resolveIssues(issueTypeValue, selectedCategory);
      const timezone =
        (channel?.address ? getChannelTimezone(channel.address) : null) ??
        getBrowserTimeZone();

      const teamsWithBlocks = savedTeams.map(team => ({
        team,
        blocks: team.coverageBlocks.map(block => ({
          daysOfWeek: toProtoDayNames(block.daysOfWeek),
          startMinute: minutesOfDay(block.startTime),
          endMinute: minutesOfDay(block.endTime),
        })),
      }));

      const teams = teamsWithBlocks.map(({ team, blocks }) => ({
        teamId: team.teamId,
        blocks,
      }));

      const issuesForRows: (string | null)[] =
        issues.length > 0 ? issues : [null];

      optimisticRows = teamsWithBlocks.flatMap(({ team, blocks }) =>
        blocks.flatMap(block =>
          issuesForRows.map(issue => ({
            id: uuidv4(),
            routingId: uuidv4(),
            category: categoryValue,
            issue,
            teamId: team.teamId,
            teamName: team.teamLabel,
            daysOfWeek: block.daysOfWeek,
            startMinute: block.startMinute,
            endMinute: block.endMinute,
            timezone,
          }))
        )
      );

      onOptimisticAdd(optimisticRows);

      await runCreateCoverageHours({
        variables: {
          input: {
            channelId,
            category: categoryValue,
            issues,
            timezone,
            teams,
          },
        },
      });

      onCreateSuccess();
      handleClose();
    } catch {
      if (optimisticRows.length > 0) {
        onCreateError(optimisticRows.map(row => row.id));
      }

      setSaveError(true);
    }
  };

  return (
    <SidePanel
      id="create-coverage-hours-panel"
      isOpen={isOpen}
      onClose={isSaving ? undefined : handleClose}
      header={t(TRANSLATION_KEYS.title)}
      size="medium"
      backdropEnabled
      footerActions={
        <CoverageHoursPanelFooter
          isConfirmingClose={isConfirmingClose}
          onDiscardChanges={handleClose}
          onKeepEditing={() => setIsConfirmingClose(false)}
          onCancel={handleOnCancel}
          onSave={handleSave}
          isSaving={isSaving}
          canSave={canSave}
        />
      }
    >
      {saveError && (
        <Alert type="error">{t(TRANSLATION_KEYS.saveError)}</Alert>
      )}
      <CreateCoverageHours
        categories={categories}
        categoryValue={categoryValue}
        onCategoryChange={handleCategoryChange}
        issueTypeValue={issueTypeValue}
        onIssueTypeChange={setIssueTypeValue}
        assignableTeams={assignableTeams}
        savedTeams={savedTeams}
        onSaveTeam={handleSaveTeam}
      />
    </SidePanel>
  );
}
